import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';

interface CreditsState {
    credits: number;
    subscriptionTier: 'free' | 'pro' | 'unlimited';
    subscriptionStatus: 'active' | 'canceled' | 'past_due' | 'incomplete';
    currentPeriodEnd: string | null;
    isLoading: boolean;
}

interface CreditsContextType extends CreditsState {
    refreshCredits: () => Promise<void>;
    hasEnoughCredits: (amount?: number) => boolean;
    isUnlimited: boolean;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export function CreditsProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [state, setState] = useState<CreditsState>({
        credits: 5,
        subscriptionTier: 'free',
        subscriptionStatus: 'active',
        currentPeriodEnd: null,
        isLoading: true,
    });

    const refreshCredits = useCallback(async () => {
        if (!user || user.is_anonymous) {
            setState((s) => ({ ...s, isLoading: false }));
            return;
        }

        try {
            const { data, error } = await supabase.functions.invoke('get-credits');

            if (error) {
                console.error('Error fetching credits:', error);
                setState((s) => ({ ...s, isLoading: false }));
                return;
            }

            setState({
                credits: data.credits ?? 5,
                subscriptionTier: data.subscription_tier ?? 'free',
                subscriptionStatus: data.subscription_status ?? 'active',
                currentPeriodEnd: data.current_period_end ?? null,
                isLoading: false,
            });
        } catch (error) {
            console.error('Error fetching credits:', error);
            setState((s) => ({ ...s, isLoading: false }));
        }
    }, [user]);

    // Fetch credits on mount and when user changes
    useEffect(() => {
        refreshCredits();
    }, [refreshCredits]);

    // Subscribe to realtime updates on user_profiles
    useEffect(() => {
        if (!user || user.is_anonymous) return;

        const channel = supabase
            .channel('credits-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'user_profiles',
                    filter: `id=eq.${user.id}`,
                },
                (payload) => {
                    const newData = payload.new as {
                        credits?: number;
                        subscription_tier?: string;
                        subscription_status?: string;
                        current_period_end?: string;
                    };
                    setState((s) => ({
                        ...s,
                        credits: newData.credits ?? s.credits,
                        subscriptionTier: (newData.subscription_tier as CreditsState['subscriptionTier']) ?? s.subscriptionTier,
                        subscriptionStatus: (newData.subscription_status as CreditsState['subscriptionStatus']) ?? s.subscriptionStatus,
                        currentPeriodEnd: newData.current_period_end ?? s.currentPeriodEnd,
                    }));
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user]);

    const hasEnoughCredits = useCallback(
        (amount = 0.25) => {
            if (state.subscriptionTier === 'unlimited') return true;
            return state.credits >= amount;
        },
        [state.credits, state.subscriptionTier]
    );

    const isUnlimited = state.subscriptionTier === 'unlimited';

    return (
        <CreditsContext.Provider
            value={{
                ...state,
                refreshCredits,
                hasEnoughCredits,
                isUnlimited,
            }}
        >
            {children}
        </CreditsContext.Provider>
    );
}

export function useCredits() {
    const context = useContext(CreditsContext);
    if (context === undefined) {
        throw new Error('useCredits must be used within a CreditsProvider');
    }
    return context;
}
